import {
    MAX_ITEM_COUNT_FOR_EXPORT,
    MAX_PAGE_SIZE_FOR_COMPARISON,
    STATISTICS,
} from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import type { StatisticsPage } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type { MonTableCollection } from '@client/ng2/models/table-collection.interface';
import type {
    SiteAnalyticsDataRequest,
    SiteAnalyticsDataResult,
    SiteAnalyticsLinkCell,
    SiteAnalyticsScaffoldConfig,
    SiteAnalyticsTableRow,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-scaffold.type';
import type {
    SiteAnalyticsPageDefinition,
    SiteAnalyticsPageDefinitionDeps,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-page-definition.type';

const LEAST_POPULAR_PAGES_CONFIG: SiteAnalyticsScaffoldConfig = {
    pageHeaderKey: 'siteAnalytics.leastPopular.header',
    pageSubheaderKey: 'siteAnalytics.leastPopular.subheader',
    tableKey: 'site-analytics-least-popular',
    dataSource: 'leastPopularPages',
    exportCategory: 'least_popular_pages',
    exportCategoryRefMaxItems: MAX_ITEM_COUNT_FOR_EXPORT,
    searchPlaceholderKey: 'siteAnalytics.scaffold.search.placeholder',
    emptyMessageKey: 'siteAnalytics.scaffold.table.emptyMessage',
    emptySearchMessageKey: 'siteAnalytics.scaffold.table.emptySearchMessage',
    tableCaptionKey: 'siteAnalytics.leastPopular.table.caption',
    exportButtonLabelKey: 'siteAnalytics.scaffold.export.buttonLabel',
    dateRangeLabelKey: 'siteAnalytics.scaffold.dateRange.label',
    compareToggleLabelKey: 'siteAnalytics.scaffold.compare.toggleLabel',
    compareDateRangeLabelKey: 'siteAnalytics.scaffold.compare.dateRangeLabel',
    defaultSortBy: 'visits',
    defaultSortDir: 'asc',
    comparisonMaxPageSize: MAX_PAGE_SIZE_FOR_COMPARISON,
    chart: {
        titleKey: 'siteAnalytics.leastPopular.chart.title',
        primarySeriesLabelKey: 'siteAnalytics.scaffold.chart.series.currentVisits',
        comparisonSeriesLabelKey: 'siteAnalytics.scaffold.chart.series.comparisonVisits',
    },
    columns: [
        {
            id: 'page',
            headerKey: 'siteAnalytics.scaffold.table.column.url',
            displayType: 'link',
            width: 'xl',
            ariaLabelKey: 'siteAnalytics.scaffold.table.urlAriaLabel{{url}}',
        },
        {
            id: 'visits',
            headerKey: 'siteAnalytics.scaffold.table.column.visits',
            displayType: 'number',
            sortable: true,
            sortField: 'visits',
            width: 'md',
        },
        {
            id: 'visitsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.visitsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'pageviews',
            headerKey: 'siteAnalytics.scaffold.table.column.pageviews',
            displayType: 'number',
            sortable: false,
            sortField: 'pageviews',
            width: 'md',
        },
        {
            id: 'pageviewsComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.pageviewsComparison',
            displayType: 'number',
            visibility: 'comparison',
            width: 'md',
        },
        {
            id: 'avgTime',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTime',
            displayType: 'duration',
            sortable: false,
            sortField: 'avg_time',
            width: 'md',
        },
        {
            id: 'avgTimeComparison',
            headerKey: 'siteAnalytics.scaffold.table.column.averageTimeComparison',
            displayType: 'duration',
            visibility: 'comparison',
            width: 'md',
        },
    ],
};

const loadLeastPopularPagesData = async (
    request: SiteAnalyticsDataRequest,
    deps: SiteAnalyticsPageDefinitionDeps,
): Promise<SiteAnalyticsDataResult> => {
    const { statsRepoService, statisticsParamsService } = deps;
    const isComparisonMode = Boolean(request.comparisonDateRange);

    const currentParams = statisticsParamsService.buildListParams({
        limit: request.perPage,
        page: request.page,
        domainId: request.domainId,
        dateRange: request.dateRange,
        dateFormat: STATISTICS.DATE_FORMAT,
        sortBy: request.sortBy,
        sortDirection: request.sortDir,
        search: request.search,
    });

    const summaryParams = statisticsParamsService.buildSummaryParams(
        request.domainId,
        request.dateRange,
        STATISTICS.DATE_FORMAT,
    );

    if (!isComparisonMode || !request.comparisonDateRange) {
        const [currentPagesResponse, summaryForCurrentResponse] = await Promise.all([
            statsRepoService.getLeastPopularPages(currentParams, contextTokens.NO_PARSE_SEARCH),
            statsRepoService.getSummary(summaryParams).catch(() => null),
        ]);

        const normalizedCurrentRows = normalizeRows(currentPagesResponse);

        return {
            rows: normalizedCurrentRows,
            chart: {
                labels: normalizedCurrentRows.map((row) => getPageLabel(row)),
                primaryValues: normalizedCurrentRows.map((row) => toInteger(row.visits)),
            },
            summary: summaryForCurrentResponse,
            total: currentPagesResponse.total ?? normalizedCurrentRows.length,
            perPage: currentPagesResponse.perPage ?? request.perPage,
            page: currentPagesResponse.currentPage ?? request.page,
        };
    }

    const comparisonParams = statisticsParamsService.buildComparisonParams(
        currentParams,
        request.comparisonDateRange,
        STATISTICS.DATE_FORMAT,
    );

    const [currentPagesComparisonResponse, comparisonPagesResponse, summaryForComparisonResponse] = await Promise.all([
        statsRepoService.getLeastPopularPages(currentParams, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getLeastPopularPages(comparisonParams, contextTokens.NO_PARSE_SEARCH),
        statsRepoService.getSummary(summaryParams).catch(() => null),
    ]);

    const normalizedComparisonRows = normalizeRows(currentPagesComparisonResponse, comparisonPagesResponse);

    return {
        rows: normalizedComparisonRows,
        chart: {
            labels: normalizedComparisonRows.map((row) => getPageLabel(row)),
            primaryValues: normalizedComparisonRows.map((row) => toInteger(row.visits)),
            comparisonValues: normalizedComparisonRows.map((row) => toInteger(row.visitsComparison)),
        },
        summary: summaryForComparisonResponse,
        total: currentPagesComparisonResponse.total ?? normalizedComparisonRows.length,
        perPage: currentPagesComparisonResponse.perPage ?? request.perPage,
        page: currentPagesComparisonResponse.currentPage ?? request.page,
    };
};

function normalizeRows (
    currentPages: MonTableCollection<StatisticsPage>,
    comparisonPages?: MonTableCollection<StatisticsPage>,
): SiteAnalyticsTableRow[] {
    const comparisonByUrl = new Map<string, StatisticsPage>();

    if (comparisonPages) {
        for (const page of comparisonPages) {
            const urlKey = String(page.url || '');
            if (!comparisonByUrl.has(urlKey)) {
                comparisonByUrl.set(urlKey, page);
            }
        }
    }

    return currentPages.map((page, index) => {
        const url = String(page.url || '');
        const comparisonPage = comparisonByUrl.get(url);
        const title = String(page.title || '');

        return {
            id: `${url || 'row'}-${index}`,
            page: {
                label: url,
                href: url,
                ...(title && title !== url ? { tooltip: title } : {}),
            },
            visits: toInteger(page.visits),
            visitsComparison: comparisonPage ? toInteger(comparisonPage.visits) : null,
            pageviews: toInteger(page.pageviews),
            pageviewsComparison: comparisonPage ? toInteger(comparisonPage.pageviews) : null,
            avgTime: toInteger(page.avg_time),
            avgTimeComparison: comparisonPage ? toInteger(comparisonPage.avg_time) : null,
        };
    });
}

function getPageLabel (row: SiteAnalyticsTableRow): string {
    const linkCell = toLinkCell(row.page);
    if (linkCell) {
        return getUrlPath(linkCell.label);
    }

    if (typeof row.page === 'string') {
        return getUrlPath(row.page);
    }

    return '';
}

function getUrlPath (value: string): string {
    try {
        const url = new URL(value);

        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return value;
    }
}

function toLinkCell (value: unknown): SiteAnalyticsLinkCell | null {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const link = value as { label?: unknown; href?: unknown; tooltip?: unknown };
    if (typeof link.label !== 'string' || typeof link.href !== 'string') {
        return null;
    }

    return {
        label: link.label,
        href: link.href,
        ...(typeof link.tooltip === 'string' && link.tooltip ? { tooltip: link.tooltip } : {}),
    };
}

function toInteger (value: unknown): number {
    const parsed = Number.parseInt(String(value ?? 0), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

export const LEAST_POPULAR_PAGES_DEFINITION: SiteAnalyticsPageDefinition = {
    key: 'leastPopularPages',
    config: LEAST_POPULAR_PAGES_CONFIG,
    loadData: loadLeastPopularPagesData,
};

export const LEAST_POPULAR_PAGE_CONFIG = LEAST_POPULAR_PAGES_DEFINITION.config;
